import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { MenuItem, Select, TableCell, Typography } from "@mui/material";
import { DashboardButton } from "../../../../common/DashboardButton";
import { StyledTable, TableHeader, TableWrapper } from "../../../style";
import { adminDashboardRoute } from "../../../../../utils/routes/Routes";
import RowContainer from "../../../../common/RowContainer";
import { useUpdateSingleProjectMutation } from "../../../../../redux/features/projects/projectsApi";
import MultipleFilterComponent from "../../../../common/MultipleFilterComponent";
import { FilterAlt, FilterAltOff, Info } from "@mui/icons-material";
import { changePageNumberProjects } from "../../../../../redux/features/projects/projectsSlice";
import { FormatDateTime } from "../../../../../utils/helpers/FormatDateTime";
import { RenderColoredCircleIconWithStatus } from "../../../../common/RenderColoredCircleIconWithStatus";
import { FormattedMessage } from "react-intl";
import { FilterButton } from "../../../../common/FilterButton";

const UserProjectsList = ({
  tableHeaders,
  onProjectsFilterStateChange,
  projects,
  paginationData,
  notFilteredSlaveProjects,
  refetchSlaveProjects,
  onProjectsPageChange,
}) => {
  // INIT
  const { id } = useParams();
  const dispatch = useDispatch();

  // REDUX
  const [updateProject, { isLoading, isSuccess }] =
    useUpdateSingleProjectMutation();

  // STATE
  const [projectStatus, setProjectStatus] = useState([]);
  const [editingRowId, setEditingRowId] = useState([]);
  const [isFilteringActive, setIsFilteringActive] = useState(false);
  const [isFilteringManualProjectsActive, setIsFilteringManualProjectsActive] =
    useState(false);

  const [filters, setFilters] = useState({
    project_status: null,
    vehicle_type: null,
    brand: null,
    model: null,
    created_at: null,
    modified_at: null,
  });
  const [manualProjectsFilters, setManualProjectsFilters] = useState({
    brand: null,
    model: null,
  });

  // EFFECTS
  useEffect(() => {
    if (!isFilteringActive) {
      resetFiltersAndPage();
      onProjectsPageChange(1);
    }
  }, [isFilteringActive]);

  // FUNCTIONS
  const handleProjectStatusChange = (event, id) => {
    event.preventDefault();
    setProjectStatus((prevState) => {
      prevState = projectStatus.filter((proj) => proj.id !== id);
      return [...prevState, { id: id, status: event.target.value }];
    });
    setEditingRowId((prevState) =>
      prevState.includes(id) ? prevState : [...prevState, id]
    );
    event.stopPropagation();
  };

  const resetFiltersAndPage = useCallback(() => {
    setFilters({
      project_status: null,
      vehicle_type: null,
      brand: null,
      model: null,
      created_at: null,
      modified_at: null,
    });
    setManualProjectsFilters({
      brand: null,
      model: null,
    });
    onProjectsFilterStateChange({
      project_status: null,
      vehicle_type: null,
      brand: null,
      model: null,
      created_at: null,
      modified_at: null,
    });
  }, [dispatch, onProjectsFilterStateChange]);

  const handleToggleFilters = () => {
    setIsFilteringActive((prevState) => {
      const newState = !prevState;
      if (newState) {
        resetFiltersAndPage();
      }
      return newState;
    });
    setIsFilteringManualProjectsActive(false);
  };

  const handleToggleManualFilters = () => {
    setIsFilteringManualProjectsActive((prevState) => {
      const newState = !prevState;
      if (newState) {
        resetFiltersAndPage();
        setIsFilteringActive(false);
      }
      return newState;
    });
    setIsFilteringActive(false);
  };
  const handleFilterChange = (key, value) => {
    // lift the state up to the parent (UserPage)
    const newFilterValue = value;

    setFilters((prevState) => {
      return { ...prevState, [key]: newFilterValue };
    });
    onProjectsFilterStateChange((filters) => ({
      ...filters,
      [key]: newFilterValue,
    }));
    dispatch(changePageNumberProjects(1));
  };

  const handleFilterChangeManual = (key, value) => {
    setManualProjectsFilters((prevState) => {
      return { ...prevState, [key]: value };
    });
    onProjectsFilterStateChange((filters) => ({
      ...filters,
      text_field: value,
    }));
  };

  const handleSave = async (projId) => {
    const objectToSave = projectStatus.find((proj) => proj.id === projId);
    const { status } = objectToSave;

    const formData = new FormData();
    formData.append("project_status", status);

    try {
      const result = await updateProject({
        projectId: projId,
        userId: id,
        formData: formData,
      }).unwrap();
      if (result) {
        setEditingRowId((prevState) => prevState.filter((id) => id !== projId));
      }
    } catch (error) {
      console.error("Failed to update project:", error);
    }
  };

  const handleCancel = (rowId) => {
    setEditingRowId((prevState) => prevState.filter((id) => id !== rowId));
    setProjectStatus((prevState) => {
      return prevState.filter((proj) => proj.id !== rowId);
    });
  };

  const renderProjectRows = () => {
    return projects?.paginated_results?.map((row, index) => {
      const projTextfieldArray =
        row?.project_textfield && row.project_textfield.split(", ");
      return (
        <tr key={index}>
          {/* <TableCell>{row?.id}</TableCell> */}
          <TableCell>
            <RenderColoredCircleIconWithStatus string={row?.status} />
          </TableCell>
          <TableCell>{row?.v_type?.name || projTextfieldArray[0]}</TableCell>
          <TableCell>{row?.brand?.name || projTextfieldArray[1]}</TableCell>
          <TableCell>{row?.model?.name || projTextfieldArray[2]}</TableCell>
          <TableCell>
            {<FormatDateTime value={row?.created_at} /> || "-"}
          </TableCell>
          <TableCell>
            {<FormatDateTime value={row?.modified_at} /> || "-"}
          </TableCell>

          <TableCell>
            <Select
              sx={{
                "& .MuiOutlinedInput-input": {
                  padding: "10px",
                  width: "110px",
                  fontSize: "14px",
                },
                "& .MuiSvgIcon-root": {
                  color: "var(--main-color)",
                },
              }}
              value={
                projectStatus?.find((proj) => proj.id === row.id)?.status ||
                row.status
              }
              onChange={(event, projectId) =>
                handleProjectStatusChange(event, row.id)
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    width: "auto",
                    backgroundColor: "var(--bkg-secondary)",
                    "& .MuiMenuItem-root": {
                      fontSize: "14px",
                    },

                    "& .MuiMenuItem-root.Mui-selected": {
                      backgroundColor: "transparent",
                    },

                    "& .MuiMenuItem-root:hover": {
                      backgroundColor: "var(--bkg-tertiary)",
                    },
                  },
                },
              }}
            >
              <MenuItem value="new">
                <FormattedMessage id="status.new" />
              </MenuItem>
              <MenuItem value="completed">
                <FormattedMessage id="status.completed" />
              </MenuItem>
              <MenuItem value="in_progress">
                <FormattedMessage id="status.inProgress" />
              </MenuItem>
              <MenuItem value="pending">
                <FormattedMessage id="status.pending" />
              </MenuItem>
              <MenuItem value="refunded">
                <FormattedMessage id="status.refunded" />
              </MenuItem>
              <MenuItem value="failed">
                <FormattedMessage id="status.failed" />
              </MenuItem>
            </Select>
          </TableCell>
          <TableCell size="small">
            <RowContainer>
              <Link
                to={`${adminDashboardRoute.users}/${id}/projects/${row?.id}`}
              >
                <DashboardButton type="filled">
                  <FormattedMessage id="button.details" />
                </DashboardButton>
              </Link>
            </RowContainer>
          </TableCell>

          <TableCell>
            <RowContainer style={{ width: "200px" }}>
              {editingRowId?.includes(row.id) ? (
                <RowContainer start halfGap>
                  <DashboardButton
                    type="filled"
                    onClick={() => handleSave(row.id)}
                  >
                    {" "}
                    <FormattedMessage id="button.save" />
                  </DashboardButton>
                  <DashboardButton
                    type="outlined"
                    onClick={() => handleCancel(row.id)}
                  >
                    <FormattedMessage id="button.cancel" />
                  </DashboardButton>
                </RowContainer>
              ) : null}
            </RowContainer>
          </TableCell>
        </tr>
      );
    });
  };

  // HELPERS

  return (
    <TableWrapper className="projects-table">
      <RowContainer start align="center">
        <RowContainer align="start" minGap className="project-filter">
          <RowContainer align="center" noGap start>
            <FilterButton
              isActive={isFilteringActive}
              disabled={
                projects?.paginated_results?.length === 0 ||
                projects?.paginated_results === null
              }
              onClick={handleToggleFilters}
            >
              <RowContainer center minGap>
                {isFilteringActive ? <FilterAlt /> : <FilterAltOff />}
                <Typography
                  variant="p"
                  color="var(--text-primary) !important"
                  fontWeight="400 !important"
                  fontSize={"16px !important"}
                >
                  <FormattedMessage id="button.filter" />
                </Typography>
              </RowContainer>
            </FilterButton>
          </RowContainer>
        </RowContainer>

        <RowContainer align="center" noGap>
          <div>
            <RowContainer align="center" noGap start>
              <FilterButton
                isActive={isFilteringManualProjectsActive}
                onClick={handleToggleManualFilters}
                disabled={
                  projects?.paginated_results?.length === 0 ||
                  projects?.paginated_results === null
                }
              >
                <RowContainer center minGap>
                  {isFilteringManualProjectsActive ? (
                    <FilterAlt />
                  ) : (
                    <FilterAltOff />
                  )}
                  <Typography
                    variant="p"
                    color="var(--text-primary) !important"
                    fontWeight="400 !important"
                    fontSize={"16px !important"}
                  >
                    <FormattedMessage id="button.filter.manual" />
                  </Typography>
                </RowContainer>
              </FilterButton>
            </RowContainer>
          </div>
        </RowContainer>
      </RowContainer>
      {isFilteringActive ? (
        <MultipleFilterComponent
          filters={filters}
          filterOptions={projects.filter_options}
          handleFilterChange={handleFilterChange}
          tableData={notFilteredSlaveProjects || []}
          paginationData={paginationData}
        />
      ) : !!isFilteringManualProjectsActive ? (
        <MultipleFilterComponent
          filters={manualProjectsFilters}
          filterOptions={projects.filter_options_manual}
          handleFilterChange={handleFilterChangeManual}
          isManualFiltering={true}
          tableData={notFilteredSlaveProjects || []}
          paginationData={paginationData}
        />
      ) : null}

      {projects?.paginated_results?.length > 0 ? (
        <StyledTable>
          <thead>
            <tr>
              {tableHeaders.map((header, index) => (
                <TableHeader key={index}>{header}</TableHeader>
              ))}
            </tr>
          </thead>
          <tbody>{renderProjectRows()}</tbody>
        </StyledTable>
      ) : (
        <RowContainer center>
          <Typography
            variant="h6"
            component="h6"
            fontWeight={"400"}
            color="var(--text-secondary)"
          >
            <FormattedMessage id="table.noData" />
          </Typography>
        </RowContainer>
      )}
    </TableWrapper>
  );
};

export default UserProjectsList;
